import { useI18n } from '@ecom/i18n';
import { Card, type BadgeTone } from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useActionableOrdersCountQuery } from '../api';

import { useInventoryOverviewQuery } from '@/features/inventory/api';
import { useCouponsQuery } from '@/features/promotions/api';

const COUPON_EXPIRY_WINDOW_DAYS = 7;

/** Static, fully-literal tone → dot-class lookup — see KpiCard.tsx's own header for why. */
const DOT_CLASSES: Record<BadgeTone, string> = {
  good: 'bg-good',
  warn: 'bg-warn',
  critical: 'bg-critical',
  info: 'bg-info',
  accent: 'bg-accent',
  neutral: 'bg-inkFaint',
};

interface AlertItem {
  readonly id: string;
  readonly tone: BadgeTone;
  readonly title: string;
  readonly description: string;
  readonly href: string;
}

/**
 * "Bloc Alertes" — Sprint UX/UI Merchant Dashboard V2: stock faible,
 * commandes à traiter, coupons expirant bientôt, chacun tiré d'un
 * endpoint réel déjà utilisé ailleurs dans l'app (jamais une donnée
 * inventée) :
 *   - stock faible/rupture   → useInventoryOverviewQuery() (le même hook
 *     que la page Inventaire, maintenant corrigé pour inclure les
 *     produits simples — voir features/inventory/api.ts)
 *   - commandes à traiter    → useActionableOrdersCountQuery() (les
 *     mêmes statuts que OrderTable.tsx's nextMerchantStatus)
 *   - coupons bientôt expirés → useCouponsQuery(), filtré sur
 *     `validUntil` (le seul champ réel de fin de validité)
 */
export function AlertsPanel(): ReactElement {
  const { t } = useI18n();
  const inventory = useInventoryOverviewQuery();
  const actionableOrders = useActionableOrdersCountQuery();
  const couponsQuery = useCouponsQuery();

  const isPending = inventory.isPending || actionableOrders.isPending || couponsQuery.isPending;

  const lowStockRows = inventory.rows.filter(
    (row) => row.stock.state === 'low_stock' || row.stock.state === 'out_of_stock',
  );
  const outOfStockCount = lowStockRows.filter((row) => row.stock.state === 'out_of_stock').length;

  const now = Date.now();
  const expiryThreshold = now + COUPON_EXPIRY_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  const expiringCoupons = (couponsQuery.data ?? []).filter((coupon) => {
    if (!coupon.isActive || coupon.validUntil === null) return false;
    const expiresAt = new Date(coupon.validUntil).getTime();
    return expiresAt >= now && expiresAt <= expiryThreshold;
  });

  const items: AlertItem[] = [];

  if (lowStockRows.length > 0) {
    items.push({
      id: 'low-stock',
      tone: outOfStockCount > 0 ? 'critical' : 'warn',
      title:
        lowStockRows.length === 1
          ? t('merchant.dashboard.alerts.lowStockOne')
          : t('merchant.dashboard.alerts.lowStockMany', { count: lowStockRows.length }),
      description:
        outOfStockCount > 0
          ? t('merchant.dashboard.alerts.lowStockOutOfStock', { count: outOfStockCount })
          : t('merchant.dashboard.alerts.lowStockBelowThreshold'),
      href: '/inventory',
    });
  }

  if (actionableOrders.count > 0) {
    items.push({
      id: 'actionable-orders',
      tone: 'info',
      title:
        actionableOrders.count === 1
          ? t('merchant.dashboard.alerts.actionableOrdersOne')
          : t('merchant.dashboard.alerts.actionableOrdersMany', {
              count: actionableOrders.count,
            }),
      description: t('merchant.dashboard.alerts.actionableOrdersDescription'),
      href: '/orders',
    });
  }

  if (expiringCoupons.length > 0) {
    items.push({
      id: 'expiring-coupons',
      tone: 'warn',
      title:
        expiringCoupons.length === 1
          ? t('merchant.dashboard.alerts.expiringCouponsOne')
          : t('merchant.dashboard.alerts.expiringCouponsMany', {
              count: expiringCoupons.length,
            }),
      description: t('merchant.dashboard.alerts.expiringCouponsDescription', {
        days: COUPON_EXPIRY_WINDOW_DAYS,
      }),
      href: '/promotions',
    });
  }

  return (
    <Card className="p-5">
      <h3 className="font-display text-sm font-bold text-ink">
        {t('merchant.dashboard.alerts.title')}
      </h3>

      {isPending ? (
        <p className="mt-3 text-sm text-inkFaint">{t('merchant.dashboard.alerts.checking')}</p>
      ) : items.length === 0 ? (
        <p className="mt-3 text-sm text-inkFaint">{t('merchant.dashboard.alerts.allClear')}</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-3">
          {items.map((item) => (
            <li key={item.id}>
              <Link
                to={item.href}
                className="flex items-start gap-2.5 rounded-lg border border-line px-3 py-2.5 transition-colors hover:border-accent/50"
              >
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT_CLASSES[item.tone]}`}
                  aria-hidden="true"
                />
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-ink">{item.title}</span>
                  <span className="block text-xs text-inkFaint">{item.description}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
